class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
  }

  getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  saveItems(items) {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
    this._updateCountBubbles(items.length);
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  hasItem(productId) {
    return this.getItems().some((item) => item.id === productId);
  }

  toggle(product) {
    const items = this.getItems();
    const index = items.findIndex((item) => item.id === product.id);
    if (index === -1) {
      items.push(product);
      this.saveItems(items);
      return true;
    } else {
      items.splice(index, 1);
      this.saveItems(items);
      return false;
    }
  }

  _updateCountBubbles(count) {
    document.querySelectorAll('.wishlist-count-bubble').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
}

window.wishlist = new Wishlist();

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = parseInt(this.dataset.productId);
    this.productData = {
      id: this.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
      url: this.dataset.productUrl,
    };

    this.btn = this.querySelector('button');
    this._updateState();

    this._clickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.wishlist.toggle(this.productData);
      this._updateState();
      this.btn.classList.remove('wishlist-btn--pop');
      void this.btn.offsetWidth; // reflow to restart animation
      this.btn.classList.add('wishlist-btn--pop');
    };

    this._wishlistHandler = () => this._updateState();

    this.btn.addEventListener('click', this._clickHandler);
    document.addEventListener('wishlist:updated', this._wishlistHandler);
  }

  disconnectedCallback() {
    if (this.btn) this.btn.removeEventListener('click', this._clickHandler);
    document.removeEventListener('wishlist:updated', this._wishlistHandler);
  }

  _updateState() {
    const active = window.wishlist.hasItem(this.productId);
    this.btn.classList.toggle('wishlist-btn--active', active);
    this.btn.setAttribute('aria-pressed', active);
    this.btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }
}

customElements.define('wishlist-button', WishlistButton);

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.emptyText =
      this.dataset.emptyText || "You haven't added any products to your wishlist yet.";
    this.shopUrl = this.dataset.shopUrl || '';
    this._render();
    this._wishlistHandler = () => this._render();
    document.addEventListener('wishlist:updated', this._wishlistHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this._wishlistHandler);
  }

  _render() {
    const items = window.wishlist.getItems();
    if (items.length === 0) {
      this.innerHTML = `
        <div class="wishlist-empty">
          <p>${this.emptyText}</p>
          <a href="${this.shopUrl}/collections/all" class="button">Continue Shopping</a>
        </div>`;
      return;
    }

    this.innerHTML = `
      <ul class="product-grid grid grid--2-col-tablet-down grid--4-col-desktop" role="list">
        ${items.map((item) => this._renderCard(item)).join('')}
      </ul>`;

    this._bindAtcButtons();
  }

  _bindAtcButtons() {
    this.querySelectorAll('.wishlist-atc-btn').forEach((btn) => {
      btn.addEventListener('click', () => this._addToCart(btn.dataset.handle, btn));
    });
  }

  async _addToCart(handle, btn) {
    btn.disabled = true;
    btn.textContent = 'Loading...';

    try {
      const res = await fetch(`/products/${handle}.js`);
      const product = await res.json();

      // Multiple variants — can't add without selection, go to product page
      if (product.variants.length > 1) {
        window.location.href = `/products/${handle}`;
        return;
      }

      const variant = product.variants[0];

      if (!variant.available) {
        btn.textContent = 'Sold Out';
        btn.disabled = true;
        return;
      }

      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 }),
      });

      if (!cartRes.ok) throw new Error('Cart add failed');

      // Update cart count bubble
      const cart = await fetch('/cart.js').then((r) => r.json());
      document.querySelectorAll('.cart-count-bubble').forEach((el) => {
        el.removeAttribute('hidden');
        const span = el.querySelector('[aria-hidden]');
        if (span) span.textContent = cart.item_count;
      });

      btn.textContent = 'Added!';
      btn.classList.add('wishlist-atc-btn--added');
      setTimeout(() => {
        btn.textContent = 'Add to cart';
        btn.classList.remove('wishlist-atc-btn--added');
        btn.disabled = false;
      }, 2000);
    } catch {
      btn.textContent = 'Add to cart';
      btn.disabled = false;
    }
  }

  _renderCard(item) {
    const heartOutlineSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg>`;
    const heartFilledSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0z"/></svg>`;

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper">
          <div class="card card--standard card--media">
            <div class="card__inner ratio" style="--ratio-percent: 125%;">
              <div class="card__media">
                <div class="media media--transparent">
                  ${item.image ? `<a href="${item.url}"><img src="${item.image}" alt="${item.title}" loading="lazy" width="533" height="666"></a>` : ''}
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <div class="card__heading-row">
                  <h3 class="card__heading h5">
                    <a href="${item.url}" class="full-unstyled-link">${item.title}</a>
                  </h3>
                  <wishlist-button
                    data-product-id="${item.id}"
                    data-product-handle="${item.handle || ''}"
                    data-product-title="${item.title || ''}"
                    data-product-image="${item.image || ''}"
                    data-product-price="${item.price || ''}"
                    data-product-url="${item.url || ''}"
                    class="wishlist-button-wrapper"
                  >
                    <button type="button" class="wishlist-btn" aria-pressed="false" aria-label="Remove from wishlist">
                      <span class="wishlist-icon-outline">${heartOutlineSvg}</span>
                      <span class="wishlist-icon-filled">${heartFilledSvg}</span>
                    </button>
                  </wishlist-button>
                </div>
                <div class="card-information">
                  <span class="price-item price-item--regular">${item.price}</span>
                </div>
                <div class="wishlist-card__atc">
                  <button
                    class="button button--primary button--full-width wishlist-atc-btn"
                    data-handle="${item.handle || ''}"
                  >
                    Add to cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>`;
  }
}

customElements.define('wishlist-page', WishlistPage);

document.addEventListener('DOMContentLoaded', () => {
  window.wishlist._updateCountBubbles(window.wishlist.getItems().length);
});
